import { ArrowRight } from "lucide-react";
import { AnimatedSection } from "@/components/ui/AnimatedSection";

// Constants for better maintainability
const HERO_CONFIG = {
  phone: "554599700570",
  whatsappMessage: "Olá, gostaria de agendar uma consulta.",
  images: {
    mobile: "/images/cuted.jpg",
    desktop: "/images/cuted.jpg"
  },
  colors: {
    primary: "#BFA15C",
    secondary: "#DFC686", 
    tertiary: "#A08A4C"
  }
} as const;

// Animation delays for consistent timing
const ANIMATION_DELAYS = {
  title: 0,
  description: 200,
  mobileImage: 300,
  buttons: 400,
  rating: 600,
  desktopImage: 800
} as const;

// Reusable component for framed image
const FramedImage = ({ 
  src, 
  alt, 
  maxWidth, 
  padding = "p-3",
  cornerSize = "w-4 h-4",
  cornerPosition = "2"
}: {
  src: string;
  alt: string;
  maxWidth: string;
  padding?: string;
  cornerSize?: string;
  cornerPosition?: string;
}) => (
  <div className="relative">
    {/* Outer glow effect with blur - extremely subtle illumination that fades to black */}
    <div className="relative p-4 bg-gradient-to-br from-[#D4AF37]/10 via-[#FFD700]/6 to-transparent rounded-2xl shadow-2xl backdrop-blur-md filter blur-[0.5px]">
      {/* Inner darker golden frame - thinner */}
      <div className="relative p-0.5 bg-gradient-to-br from-[#8B6914] to-[#654321] rounded-xl shadow-lg">
        {/* Image container */}
        <div className="overflow-hidden rounded-lg">
          <img 
            src={src}
            alt={alt}
            className={`w-full ${maxWidth} object-cover transition-transform duration-300 hover:scale-[1.02]`}
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        </div>
      </div>
    </div>
  </div>
);

const Hero = () => {
  const handleWhatsAppClick = () => {
    const whatsappUrl = `https://wa.me/${HERO_CONFIG.phone}?text=${encodeURIComponent(HERO_CONFIG.whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section 
      id="inicio" 
      className="relative bg-lawblack-950 min-h-screen flex items-center overflow-hidden"
      aria-label="Seção principal - Advocacia de Excelência"
    >
      {/* Background overlay with subtle texture and gradient */}
      <div 
        className="absolute inset-0 bg-[url('/subtle-dark-texture.png')] bg-repeat opacity-10"
        aria-hidden="true"
      />
      
      {/* Dark gradient overlay */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-lawblack-950 via-lawblack-900 to-lawblack-950"
        style={{ opacity: 0.95 }}
        aria-hidden="true"
      />

      <div className="container-custom relative z-10 py-12 pt-32">
        <div className="flex flex-col items-center text-center lg:text-left lg:items-start lg:flex-row">
          {/* Text Content */}
          <div className="lg:w-1/2 mb-12 lg:mb-0 lg:pr-10">
            <AnimatedSection>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-6 leading-tight">
                Advocacia <br />
                de <span className={`bg-gradient-to-r from-[${HERO_CONFIG.colors.primary}] via-[${HERO_CONFIG.colors.secondary}] to-[${HERO_CONFIG.colors.primary}] text-transparent bg-clip-text drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]`}>
                  Excelência
                </span> <br />
                Para Seu Caso
              </h1>
            </AnimatedSection>
            
            <AnimatedSection delay={ANIMATION_DELAYS.description}>
              <p className="text-white/70 text-lg md:text-xl mb-10 max-w-2xl mx-auto lg:mx-0 text-justify">
                Nosso escritório está comprometido em oferecer consultoria jurídica personalizada 
                e de alta qualidade, com soluções inovadoras para proteger seus interesses e garantir seus direitos.
              </p>
            </AnimatedSection>

            {/* Image Section - Only visible on mobile between text and buttons */}
            <AnimatedSection 
              className="w-full max-w-[280px] mb-10 mx-auto lg:hidden" 
              delay={ANIMATION_DELAYS.mobileImage}
            >
              <FramedImage
                src={HERO_CONFIG.images.mobile}
                alt="Advogada Priscilla Fracaro"
                maxWidth=""
              />
            </AnimatedSection>
            
            <AnimatedSection delay={ANIMATION_DELAYS.buttons}>
              <div className="flex flex-row gap-4 justify-center lg:justify-start">
                <button 
                  onClick={handleWhatsAppClick}
                  className="btn-primary flex items-center gap-2 text-sm px-4 py-3 min-w-[120px]"
                  aria-label="Entrar em contato via WhatsApp"
                >
                  Tire sua dúvida
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </button>
                
                <a 
                  href="#about" 
                  className="btn-secondary flex items-center gap-2 text-sm px-4 py-3 min-w-[120px]"
                  aria-label="Conheça mais sobre a advogada"
                >
                  Me conheça
                  <ArrowRight className="h-4 w-4 icon-gradient-gold" aria-hidden="true" />
                </a>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={ANIMATION_DELAYS.rating} className="mt-16">
              <div className={`px-4 py-2 border-2 border-[${HERO_CONFIG.colors.primary}] rounded-lg inline-flex items-center gap-3 bg-lawblack-950`}>
                <span className="text-gradient-gold text-xl" role="img" aria-label="5 estrelas">★★★★★</span>
                <div className={`border-l-2 border-[${HERO_CONFIG.colors.primary}] pl-3`}>
                  <p className="text-white text-sm">Reconhecido entre os melhores escritórios de Medianeira e região</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
          
          {/* Image Section - Only visible on desktop */}
          <div className="lg:w-1/2 relative hidden lg:block">
            <AnimatedSection delay={ANIMATION_DELAYS.desktopImage}>
              <div className="flex items-center justify-center h-full min-h-[50vh]">
                <FramedImage
                  src={HERO_CONFIG.images.desktop}
                  alt="Advogada Priscilla Fracaro"
                  maxWidth="max-w-[420px]"
                  padding="p-4"
                  cornerSize="w-6 h-6"
                  cornerPosition="3"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;


